import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResultsApi } from '@features/results/services/results-api';
import { BusinessApi } from '@features/business/services/business-api';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

interface ResultExtendedDto {
  Id: string;
  SourceId?: string;
  Variation?: string | null;
  Reference?: string | null;
  Type?: string | null;
  Category?: string | null;
  Scope?: string | number | null;
  Pcg?: string | null;
  Year?: number;
  OfficeId?: string | null;
  BusinessId?: string | null;

  Co2?: number;
  Ch4?: number;
  Ch4Bio?: number;
  N2o?: number;
  Co2Bio?: number;

  TotalT?: number;
  TotalCo2e?: number;

  CreatedAt?: string | null;
  UpdatedAt?: string | null;
}

export interface OverviewResponse {
  totalByScope: { scope: string; value: number }[];
  totalByCategory: { category: string; value: number }[];
  totalByCompany: { company: string; scope: string; value: number }[];
  totalByCompanyCategory: { company: string; category: string; value: number }[];
  totalByOffice: { office: string; value: number }[];
  totalByVariation: { variation: string; value: number }[];
  mode: 'alcance' | 'categoria';
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private resultsApi = inject(ResultsApi);
  private businessApi = inject(BusinessApi);
  private http = inject(HttpClient);
  private variationsCache: Map<string, string> | null = null;

  constructor() {}

  private parseNumberField(obj: any, ...keys: string[]): number | undefined {
    for (const k of keys) {
      if (obj == null) continue;
      const v = obj[k];
      if (v === undefined) continue;
      if (v && typeof v === 'object' && 'N' in v) {
        const n = Number(v.N);
        if (Number.isFinite(n)) return n;
      }
      if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return undefined;
  }

  private getStringField(obj: any, ...keys: string[]): string | undefined {
    for (const k of keys) {
      if (obj == null) continue;
      const v = obj[k];
      if (v === undefined) continue;
      if (v && typeof v === 'object' && 'S' in v) return String(v.S);
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
    }
    return undefined;
  }

  private normalizeRow(raw: any): ResultExtendedDto {
    const Id = this.getStringField(raw, 'Id', 'id', 'id_s') ?? (raw?.id ? String(raw.id) : '');
    const businessId = this.getStringField(raw, 'BusinessId', 'businessId', 'business_id', 'business_id_s') ?? undefined;
    const officeId = this.getStringField(raw, 'OfficeId', 'officeId', 'office_id', 'office_id_s') ?? undefined;
    const scope = this.getStringField(raw, 'Scope', 'scope', 'scope_s') ?? undefined;
    const variation = this.getStringField(raw, 'Variation', 'variation', 'variation_s') ?? undefined;
    const category = this.getStringField(raw, 'Category', 'category', 'category_s') ?? undefined;
    const yearStr = this.getStringField(raw, 'Year', 'year', 'year_s') ?? undefined;

    const co2 = this.parseNumberField(raw, 'Co2', 'co2', 'co2_n');
    const ch4 = this.parseNumberField(raw, 'Ch4', 'ch4', 'ch4_n');
    const ch4bio = this.parseNumberField(raw, 'Ch4Bio', 'ch4_bio', 'ch4_bio_n');
    const n2o = this.parseNumberField(raw, 'N2o', 'n2o', 'n2o_n');
    const co2bio = this.parseNumberField(raw, 'Co2Bio', 'co2_bio', 'co2_bio_n');

    const totalCo2e = this.parseNumberField(raw, 'TotalCo2e', 'totalCo2e', 'total_co2e', 'total_co2e_n');
    const totalT = this.parseNumberField(raw, 'TotalT', 'totalT', 'total_t', 'total_t_n');

    return {
      Id,
      BusinessId: businessId,
      OfficeId: officeId,
      Scope: scope,
      Variation: variation,
      Category: category,
      Year: yearStr ? Number(yearStr) : undefined,

      Co2: co2,
      Ch4: ch4,
      Ch4Bio: ch4bio,
      N2o: n2o,
      Co2Bio: co2bio,

      TotalCo2e: totalCo2e,
      TotalT: totalT
    };
  }

  private getCategoryName(category: string): string {
    return `Categoría ${category}`;
  }

  private getVariationLabel(variationKey: string): string {
    if (!this.variationsCache) {
      return variationKey;
    }
    return this.variationsCache.get(variationKey) ?? variationKey;
  }

  private loadVariations(): Observable<Map<string, string>> {
    if (this.variationsCache) {
      return of(this.variationsCache);
    }

    return this.http.get<any>('assets/data/sources.json').pipe(
      map((data) => {
        const variationsData = data?.variations || {};
        this.variationsCache = new Map<string, string>(Object.entries(variationsData));
        return this.variationsCache;
      }),
      catchError((err) => {
        console.error('Error loading variations from sources.json:', err);
        this.variationsCache = new Map();
        return of(this.variationsCache);
      })
    );
  }

  getOverview(filters?: Record<string, any>): Observable<OverviewResponse> {
    const params: Record<string, any> = {};
    const mode = filters?.['mode'] || 'alcance';

    if (filters?.['businessId'] && filters['businessId'] !== 'Todas') params['businessId'] = filters['businessId'];
    if (filters?.['officeId'] && filters['officeId'] !== 'Todas') params['officeId'] = filters['officeId'];

    if (filters?.['year']) params['year'] = Number(filters['year']);
    if (filters?.['pcg']) params['pcg'] = filters['pcg'];

    if (filters?.['category'] !== undefined && filters['category'] !== null) {
      const categoryValue = Number(filters['category']);
      if (!isNaN(categoryValue) && categoryValue >= 0) {
        params['category'] = categoryValue;
      }
    }

    if (filters?.['scope'] !== undefined && filters['scope'] !== null) {
      const scopeValue = Number(filters['scope']);
      if (!isNaN(scopeValue) && scopeValue >= 0) {
        params['scope'] = scopeValue;
      }
    }
    if (filters?.['variation'] && filters['variation'] !== 'Todas') params['variation'] = filters['variation'];
    params['_ts'] = Date.now();

    return this.loadVariations().pipe(
      switchMap(() =>
        forkJoin({
          resultsResp: this.resultsApi.getDashboard(params).pipe(
            catchError(err => {
              console.error('ResultsApi.getDashboard error', err);
              return of({ data: [] } as any);
            })
          ),
          businessGroupsResp: this.businessApi.getBusinessGrouped().pipe(
            catchError(err => {
              console.warn('BusinessApi.getBusinessGrouped failed', err);
              return of({ Data: [] } as any);
            })
          )
        })
      ),
      map(({ resultsResp, businessGroupsResp }) => {
        const rawRows = (resultsResp?.data ?? resultsResp?.Data ?? resultsResp) || [];
        const rawArray = Array.isArray(rawRows) ? rawRows : (rawRows?.Data && Array.isArray(rawRows.Data) ? rawRows.Data : []);

        const normalized: ResultExtendedDto[] = (rawArray || []).map((r: any) => this.normalizeRow(r));

        const businessGroups = businessGroupsResp?.Data ?? businessGroupsResp ?? [];
        const businessNameMap = new Map<string, string>();
        const officeNameMap = new Map<string, string>();

        for (const grp of (businessGroups || [])) {
          const blist = grp.Business || [];
          for (const b of blist) {
            if (b?.Id && b?.Name) businessNameMap.set(b.Id, b.Name);
            const offs = b?.Offices || [];
            for (const o of offs) {
              if (o?.Id && o?.Name) officeNameMap.set(o.Id, o.Name);
            }
          }
        }

        const byScope = new Map<string, number>();
        const byCategory = new Map<string, number>();
        const byCompanyScope = new Map<string, number>();
        const byCompanyCategory = new Map<string, number>();
        const byOffice = new Map<string, number>();
        const byVariation = new Map<string, number>();

        const getRowValue = (r: ResultExtendedDto): number => {
          if (typeof r.TotalCo2e === 'number' && Number.isFinite(r.TotalCo2e)) return r.TotalCo2e;
          if (typeof r.TotalT === 'number' && Number.isFinite(r.TotalT)) return r.TotalT;

          const sumParts =
            (Number(r.Co2 ?? 0) || 0) +
            (Number(r.Ch4 ?? 0) || 0) +
            (Number(r.N2o ?? 0) || 0) +
            (Number(r.Co2Bio ?? 0) || 0) +
            (Number(r.Ch4Bio ?? 0) || 0);

          return sumParts;
        };

        for (const r of normalized) {
          const scopeKey = String(r.Scope ?? '').trim() || '0';
          const categoryKey = String(r.Category ?? '').trim() || '0';
          const variationKey = String(r.Variation ?? '').trim() || 'Sin especificar';
          const val = Number(getRowValue(r)) || 0;

          byScope.set(scopeKey, (byScope.get(scopeKey) ?? 0) + val);

          byCategory.set(categoryKey, (byCategory.get(categoryKey) ?? 0) + val);

          byVariation.set(variationKey, (byVariation.get(variationKey) ?? 0) + val);

          const bid = r.BusinessId ?? 'UNKNOWN';

          const compScopeKey = `${bid}||${scopeKey}`;
          byCompanyScope.set(compScopeKey, (byCompanyScope.get(compScopeKey) ?? 0) + val);

          const compCatKey = `${bid}||${categoryKey}`;
          byCompanyCategory.set(compCatKey, (byCompanyCategory.get(compCatKey) ?? 0) + val);

          const oid = r.OfficeId ?? 'UNKNOWN';
          byOffice.set(oid, (byOffice.get(oid) ?? 0) + val);
        }

        let totalByScope = Array.from(byScope.entries())
          .map(([scope, value]) => ({ scope, value }));

        const requiredScopes = ['1', '2', '3'];
        for (const s of requiredScopes) {
          if (!totalByScope.some(x => String(x.scope) === s)) {
            totalByScope.push({ scope: s, value: 0 });
          }
        }

        totalByScope.sort((a, b) => {
          const na = Number(a.scope);
          const nb = Number(b.scope);
          if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a.scope).localeCompare(String(b.scope));
          return na - nb;
        });

        let totalByCategory = Array.from(byCategory.entries())
          .map(([category, value]) => ({ category, value }))
          .sort((a, b) => {
            const na = Number(a.category);
            const nb = Number(b.category);
            if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a.category).localeCompare(String(b.category));
            return na - nb;
          });

        const totalByVariation = Array.from(byVariation.entries())
          .map(([variation, value]) => ({ variation, value }))
          .sort((a, b) => b.value - a.value);

        const totalByCompany: { company: string; scope: string; value: number }[] = [];
        byCompanyScope.forEach((value, key) => {
          const [bid, scope] = key.split('||');
          const companyName = businessNameMap.get(bid) ?? bid ?? 'Sin empresa';
          totalByCompany.push({ company: companyName, scope: scope ?? '', value });
        });

        const totalByCompanyCategory: { company: string; category: string; value: number }[] = [];
        byCompanyCategory.forEach((value, key) => {
          const [bid, category] = key.split('||');
          const companyName = businessNameMap.get(bid) ?? bid ?? 'Sin empresa';
          totalByCompanyCategory.push({ company: companyName, category: category ?? '', value });
        });

        const totalByOffice = Array.from(byOffice.entries()).map(([oid, value]) => {
          const officeName = officeNameMap.get(oid) ?? oid ?? 'Sin sede';
          return { office: officeName, value };
        });

        return {
          totalByScope,
          totalByCategory,
          totalByCompany,
          totalByCompanyCategory,
          totalByOffice,
          totalByVariation,
          mode
        } as OverviewResponse;
      })
    );
  }

  getDonutSeries(resp: OverviewResponse) {
    if (resp.mode === 'categoria') {
      return {
        series: resp.totalByCategory.map(c => c.value),
        labels: resp.totalByCategory.map(c => this.getCategoryName(c.category))
      };
    }

    return {
      series: resp.totalByScope.map(s => s.value),
      labels: resp.totalByScope.map(s => `Alcance ${s.scope}`)
    };
  }

  getStackedByCompany(resp: OverviewResponse) {
    if (resp.mode === 'categoria') {
      const companies = Array.from(new Set(resp.totalByCompanyCategory.map(r => r.company)));
      const categories = Array.from(new Set(resp.totalByCompanyCategory.map(r => r.category)))
        .sort((a, b) => {
          const na = Number(a);
          const nb = Number(b);
          if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a).localeCompare(String(b));
          return na - nb;
        });

      const series = categories.map(category => ({
        name: this.getCategoryName(category),
        data: companies.map(c => {
          const found = resp.totalByCompanyCategory.find(r => r.company === c && r.category === category);
          return found ? found.value : 0;
        })
      }));
      return { series, categories: companies };
    }

    const companies = Array.from(new Set(resp.totalByCompany.map(r => r.company)));
    const scopes = Array.from(new Set(resp.totalByCompany.map(r => r.scope)))
      .sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a).localeCompare(String(b));
        return na - nb;
      });

    const series = scopes.map(scope => ({
      name: `Alcance ${scope}`,
      data: companies.map(c => {
        const found = resp.totalByCompany.find(r => r.company === c && r.scope === scope);
        return found ? found.value : 0;
      })
    }));
    return { series, categories: companies };
  }

  getVariationHorizontal(resp: OverviewResponse) {
    const sortedVariation = [...resp.totalByVariation].sort((a, b) => b.value - a.value);
    return {
      series: [{ name: 'tCO2e', data: sortedVariation.map(v => v.value) }],
      categories: sortedVariation.map(v => this.getVariationLabel(v.variation))
    };
  }

  getOfficeHorizontal(resp: OverviewResponse) {
    const sortedOffices = [...resp.totalByOffice].sort((a, b) => b.value - a.value);
    return {
      series: [{ name: 'tCO2e', data: sortedOffices.map(o => o.value) }],
      categories: sortedOffices.map(o => o.office)
    };
  }

  getMonthlySeries(resp: any) {
    const data = resp?.Data || {};
    const rawSeries = data.Series || [];
    const categories = data.Categories || [];

    const series = rawSeries.map((s: any) => ({
      name: s.Name || 'tCO2e',
      data: s.Data || []
    }));

    return { series, categories };
  }
}